import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TextB, TextItalic, TextUnderline, List, ListNumbers, Eraser, Paragraph } from '@phosphor-icons/react';
import { api } from '@/lib/api';
import type { PlaceholderDef, Suggestion, Template } from '@/lib/types';
import { cn } from '@/lib/utils';

/** Konverze legacy plain text → bezpečný HTML (escape + `\n→<br>`). */
function plainToHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\n/g, '<br>');
}

function isHtmlBody(s: string): boolean {
  return /<[a-z][\s\S]*?>/i.test(s);
}

/**
 * Editor šablony.
 *
 *   - URL `/templates/new` → prázdná šablona; uživatel vloží text, klikne „Analyzovat",
 *     vybere které návrhy přijmout, uloží.
 *   - URL `/templates/:id` → existující šablona; tělo lze upravovat, paleta vpravo
 *     pro vkládání placeholderů, „Analyzovat" znovu na aktuálním textu.
 */
export default function TemplateEditor() {
  const { id } = useParams();
  const isEditing = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [isPersonal, setIsPersonal] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  // Po prvním analyze tlačítko vypneme — opakovaná analýza po vložení tagů
  // už nedává smysl (matched substringy se nemapují do HTML).
  const [analyzeUsed, setAnalyzeUsed] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  // Načti existující šablonu při editaci.
  const tplQuery = useQuery({
    queryKey: ['template', id],
    queryFn: () => api<{ ok: true; template: Template }>(`/api/templates/${id}`),
    enabled: isEditing,
  });
  useEffect(() => {
    if (tplQuery.data?.template && !initializedRef.current) {
      const t = tplQuery.data.template;
      setName(t.name);
      setIsPersonal(!!t.isPersonal);
      // Legacy plain text → escape + <br>. Existující HTML zůstává.
      const html = isHtmlBody(t.body) ? t.body : plainToHtml(t.body);
      setBody(html);
      if (editorRef.current) editorRef.current.innerHTML = html;
      initializedRef.current = true;
    }
  }, [tplQuery.data]);

  // Paleta — kanonický seznam placeholderů.
  const palQuery = useQuery({
    queryKey: ['placeholders'],
    queryFn: () => api<{ ok: true; placeholders: PlaceholderDef[] }>('/api/settings/placeholders'),
  });

  const placeholders = palQuery.data?.placeholders || [];
  const placeholdersBySource = useMemo(() => {
    const groups: Record<string, PlaceholderDef[]> = {};
    for (const p of placeholders) {
      (groups[p.source] = groups[p.source] || []).push(p);
    }
    return groups;
  }, [placeholders]);

  async function analyze() {
    const plain = editorRef.current?.innerText || '';
    if (plain.trim().length < 20) {
      setAnalyzeError('Vlož aspoň 20 znaků textu.');
      return;
    }
    setAnalyzing(true);
    setAnalyzeError(null);
    try {
      const res = await api<{ ok: true; suggestions: Suggestion[] }>('/api/templates/analyze', {
        method: 'POST',
        json: { text: plain },
      });
      setSuggestions(res.suggestions);
      setAnalyzeUsed(true);
    } catch (e) {
      setAnalyzeError(e instanceof Error ? e.message : String(e));
    } finally {
      setAnalyzing(false);
    }
  }

  function acceptSuggestion(s: Suggestion) {
    // Suggestion má offsety v plain textu (innerText). Pro aplikaci do HTML
    // hledáme `matched` substring přímo v innerHTML — funguje, dokud uživatel
    // nezačal formátovat (matched substring není rozbitý tagy). Pokud match
    // selže, návrh ignorujeme s notifikací.
    const ed = editorRef.current;
    if (!ed) return;
    const html = ed.innerHTML;
    const replacement = s.replacement || `{${s.placeholderKey}}`;
    const idx = html.indexOf(s.matched);
    if (idx === -1) {
      // Tichý fail — návrh už nedělá smysl (text mezitím změněn).
      setSuggestions((prev) => prev.filter((p) => !(p.start === s.start && p.end === s.end)));
      return;
    }
    ed.innerHTML = html.slice(0, idx) + replacement + html.slice(idx + s.matched.length);
    setBody(ed.innerHTML);
    setSuggestions((prev) => prev.filter((p) => !(p.start === s.start && p.end === s.end)));
  }

  function declineSuggestion(s: Suggestion) {
    setSuggestions((prev) => prev.filter((p) => !(p.start === s.start && p.end === s.end)));
  }

  function insertPlaceholderAtCursor(key: string) {
    const ed = editorRef.current;
    if (!ed) return;
    ed.focus();
    // execCommand insertText vloží text na pozici kurzoru a respektuje
    // současnou selekci. Standard ho deprecuje, ale browsery ho podporují
    // a pro jeden token to bohatě stačí.
    document.execCommand('insertText', false, `{${key}}`);
    setBody(ed.innerHTML);
  }

  function exec(cmd: string, value?: string) {
    const ed = editorRef.current;
    if (!ed) return;
    ed.focus();
    document.execCommand(cmd, false, value);
    setBody(ed.innerHTML);
  }

  const save = useMutation({
    mutationFn: async (): Promise<{ ok: true; template: Template }> => {
      if (isEditing) {
        return api<{ ok: true; template: Template }>(`/api/templates/${id}`, {
          method: 'PUT',
          json: { name, body, isPersonal },
        });
      }
      return api<{ ok: true; template: Template }>('/api/templates', {
        method: 'POST',
        json: { name, body, isPersonal },
      });
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      qc.setQueryData(['template', data.template.id], data);
      navigate(`/templates/${data.template.id}`, { replace: true });
    },
  });

  const canSave = name.trim().length > 0 && body.trim().length > 0;

  return (
    <div className="space-y-5 max-w-6xl">
      <header className="flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-muted-foreground">
            <Link to="/templates" className="hover:underline">
              Šablony
            </Link>
            {' / '}
            <span>{isEditing ? name || '…' : 'Nová'}</span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1">
            {isEditing ? 'Upravit šablonu' : 'Nová šablona'}
          </h1>
        </div>
      </header>

      <div className="space-y-1.5">
        <label className="text-sm font-medium">Název šablony</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="např. „Obecná poptávka na odkup"
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
        />
      </div>

      <label className="flex items-start gap-3 cursor-pointer rounded-md border p-3 bg-muted/20">
        <input
          type="checkbox"
          checked={isPersonal}
          onChange={(e) => setIsPersonal(e.target.checked)}
          className="mt-0.5"
        />
        <div className="text-sm">
          <div className="font-medium">Osobní dopis (bez hlavičky firmy)</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Vynechá firemní záhlaví (jméno, firma, adresa) a placeholdery{' '}
            <code className="font-mono">{'{moje_telefon}'}</code> /{' '}
            <code className="font-mono">{'{moje_email}'}</code> se vyplní z osobní identity
            (Nastavení → Osobní identita).
          </div>
        </div>
      </label>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Hlavní editor */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">Text dopisu</span>
            <button
              onClick={analyze}
              disabled={analyzing || analyzeUsed}
              title={analyzeUsed ? 'Analyzovat lze jen jednou — po formátování by návrhy nesedly na text.' : undefined}
              className="ml-auto px-3 py-1.5 rounded-md border text-sm hover:bg-muted disabled:opacity-50"
            >
              {analyzing ? 'Analyzuji…' : analyzeUsed ? 'Analyzováno' : 'Analyzovat (najít proměnné)'}
            </button>
          </div>

          <FormatToolbar onExec={exec} />

          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={(e) => setBody((e.target as HTMLDivElement).innerHTML)}
            className="w-full min-h-[500px] rounded-md border bg-background p-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary/30 prose-template"
            data-placeholder={'Vlož text dopisu. Klikni „Analyzovat" a navrhnu, kde vsadit placeholdery. Formátovat lze tlačítky výše.'}
          />

          {analyzeError && (
            <div className="text-sm text-destructive">{analyzeError}</div>
          )}
          {suggestions.length > 0 && (
            <SuggestionsPanel
              suggestions={suggestions}
              onAccept={acceptSuggestion}
              onDecline={declineSuggestion}
            />
          )}
        </div>

        {/* Paleta vpravo */}
        <aside className="space-y-4">
          <div className="rounded-lg border bg-muted/20 p-3 text-xs text-muted-foreground">
            Klikni na placeholder a vloží se na pozici kurzoru v textu.
          </div>
          {Object.entries(placeholdersBySource).map(([source, items]) => (
            <div key={source}>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">
                {SOURCE_LABEL[source] || source}
              </div>
              <div className="space-y-1">
                {items.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => insertPlaceholderAtCursor(p.key)}
                    className="w-full text-left rounded-md border bg-background px-2.5 py-1.5 text-xs hover:bg-muted group"
                    title={p.description}
                  >
                    <code className="font-mono text-foreground">{`{${p.key}}`}</code>
                    <span className="block text-muted-foreground text-[11px] mt-0.5 group-hover:text-foreground transition-colors">
                      {p.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </aside>
      </div>

      <div className="flex items-center gap-3 pt-3 border-t">
        <Link to="/templates" className="px-4 py-2 rounded-md border text-sm hover:bg-muted">
          Zpět
        </Link>
        <button
          onClick={() => save.mutate()}
          disabled={!canSave || save.isPending}
          className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
        >
          {save.isPending ? 'Ukládám…' : isEditing ? 'Uložit změny' : 'Vytvořit šablonu'}
        </button>
        {save.error && (
          <span className="text-xs text-destructive">Chyba: {String(save.error)}</span>
        )}
      </div>
    </div>
  );
}

const SOURCE_LABEL: Record<string, string> = {
  derived: 'Odvozené (automaticky)',
  settings: 'Z nastavení (odesílatel)',
  owner: 'Vlastník',
  property: 'Nemovitost',
  contact: 'Kontakt',
};

function FormatToolbar({ onExec }: { onExec: (cmd: string, value?: string) => void }) {
  return (
    <div className="flex items-center gap-1 flex-wrap rounded-md border bg-muted/30 p-1 text-sm">
      <ToolbarBtn title="Tučně (Ctrl+B)" onClick={() => onExec('bold')}>
        <TextB size={14} />
      </ToolbarBtn>
      <ToolbarBtn title="Kurzíva (Ctrl+I)" onClick={() => onExec('italic')}>
        <TextItalic size={14} />
      </ToolbarBtn>
      <ToolbarBtn title="Podtrženě (Ctrl+U)" onClick={() => onExec('underline')}>
        <TextUnderline size={14} />
      </ToolbarBtn>
      <div className="w-px h-5 bg-border mx-1" />
      <ToolbarBtn title="Odstavec" onClick={() => onExec('formatBlock', 'p')}>
        <Paragraph size={14} />
      </ToolbarBtn>
      <ToolbarBtn title="Odrážky" onClick={() => onExec('insertUnorderedList')}>
        <List size={14} />
      </ToolbarBtn>
      <ToolbarBtn title="Číslovaný seznam" onClick={() => onExec('insertOrderedList')}>
        <ListNumbers size={14} />
      </ToolbarBtn>
      <div className="w-px h-5 bg-border mx-1" />
      <label
        className="inline-flex items-center gap-1.5 px-1.5 py-1 rounded hover:bg-background cursor-pointer text-xs"
        title="Barva textu"
        onMouseDown={(e) => e.preventDefault()}
      >
        <span className="w-3 h-3 rounded border bg-gradient-to-br from-red-500 via-amber-500 to-blue-500" />
        Barva
        <input
          type="color"
          defaultValue="#000000"
          onInput={(e) => onExec('foreColor', (e.target as HTMLInputElement).value)}
          className="w-4 h-4 p-0 border-0 cursor-pointer"
        />
      </label>
      <ToolbarBtn title="Odstranit formátování" onClick={() => onExec('removeFormat')}>
        <Eraser size={14} />
      </ToolbarBtn>
    </div>
  );
}

function ToolbarBtn({
  title,
  onClick,
  children,
}: {
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()} // Nepřepneme focus pryč z editoru
      onClick={onClick}
      className="px-2 py-1 rounded hover:bg-background text-foreground"
    >
      {children}
    </button>
  );
}

function SuggestionsPanel({
  suggestions,
  onAccept,
  onDecline,
}: {
  suggestions: Suggestion[];
  onAccept: (s: Suggestion) => void;
  onDecline: (s: Suggestion) => void;
}) {
  return (
    <div className="rounded-lg border">
      <div className="px-3 py-2 border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
        Návrhy ({suggestions.length})
      </div>
      <ul className="divide-y text-sm">
        {suggestions.map((s, i) => (
          <li key={i} className="p-3 flex items-start gap-3">
            <ConfidenceDot c={s.confidence} />
            <div className="flex-1 min-w-0">
              <div className="text-xs">
                <code className="bg-muted px-1.5 py-0.5 rounded">{s.matched}</code>
                <span className="text-muted-foreground mx-1.5">→</span>
                <code className="bg-primary/10 px-1.5 py-0.5 rounded text-primary">
                  {s.replacement || `{${s.placeholderKey}}`}
                </code>
              </div>
              <div className="text-xs text-muted-foreground mt-1">{s.reason}</div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <button
                onClick={() => onAccept(s)}
                className="px-2.5 py-1 rounded border border-primary/40 text-xs font-medium hover:bg-primary/10"
              >
                Vložit
              </button>
              <button
                onClick={() => onDecline(s)}
                className="px-2.5 py-1 rounded border text-xs hover:bg-muted"
              >
                Ignorovat
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ConfidenceDot({ c }: { c: Suggestion['confidence'] }) {
  const color =
    c === 'high'
      ? 'bg-emerald-500'
      : c === 'medium'
        ? 'bg-amber-500'
        : 'bg-slate-400';
  return (
    <span
      title={c === 'high' ? 'Vysoká důvěra' : c === 'medium' ? 'Střední' : 'Nízká — ověř'}
      className={cn('mt-1.5 w-2 h-2 rounded-full shrink-0', color)}
    />
  );
}
